import React, { useState } from "react";
import { Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Tooltip, IconButton } from "@mui/material";
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon, Security as SecurityIcon, Error as ErrorIcon } from "@mui/icons-material";
import { ConsentDataElement } from "../services/purpose-service";
import { personalDataService, PersonalDataItem } from "../services/personal-data-service";
import { useTranslations } from "../utils/translation";

interface PersonalDataElementProps {
  element: ConsentDataElement;
  consentHeaderId: number;
  languageCode: string;
  onPersonalDataLoaded?: (vaultId: string, originalValue: string) => void;
}

const PersonalDataElement: React.FC<PersonalDataElementProps> = ({ element, consentHeaderId, languageCode, onPersonalDataLoaded }) => {
  const t = useTranslations(languageCode);

  // State for personal data visibility and loading
  const [showPersonalData, setShowPersonalData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalValue, setOriginalValue] = useState<string | null>(null);

  const handleShowPersonalData = async () => {
    if (!element.vaultId) {
      setError("Vault ID not available for this data element");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await personalDataService.getPersonalDataValues(consentHeaderId, [element.vaultId.toString()]);

      if (response.success) {
        const vaultIdKey = element.vaultId.toString();
        const personalDataItem = personalDataService.getVaultPersonalData(response, vaultIdKey);

        if (personalDataItem && personalDataItem.success && personalDataItem.originalValue) {
          setOriginalValue(personalDataItem.originalValue);
          setShowPersonalData(true);

          // Notify parent component
          if (onPersonalDataLoaded) {
            onPersonalDataLoaded(element.vaultId.toString(), personalDataItem.originalValue);
          }
        } else {
          setError(personalDataItem?.error || "Failed to retrieve personal data");
        }
      } else {
        setError(response.error || "Failed to retrieve personal data");
      }
    } catch (err: any) {
      setError(err.message || "Failed to retrieve personal data");
    } finally {
      setLoading(false);
    }
  };

  const handleHidePersonalData = () => {
    setShowPersonalData(false);
    setOriginalValue(null);
    setError(null);
  };

  const getTranslatedElementName = (element: ConsentDataElement, languageCode: string) => {
    // First try to get translation from the element itself
    if (element.translations && element.translations.length > 0) {
      const translation = element.translations.find((t) => t.languageCode === languageCode);
      if (translation) {
        return translation.translatedName;
      }
    }

    // Fallback to original name
    return element.elementName;
  };

  const getDisplayValue = () => {
    if (showPersonalData && originalValue) {
      return originalValue;
    }
    return element.dataElementValue; // This will be "[PROTECTED]" for personal data
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">{getTranslatedElementName(element, languageCode)}</Typography>
          <Chip
            label={element.isPersonalData ? t("personalData") : t("nonPersonalData")}
            size="small"
            icon={element.isPersonalData ? <SecurityIcon /> : undefined}
            sx={{
              backgroundColor: element.isPersonalData ? "var(--bg-accent)" : "var(--bg-secondary)",
              color: element.isPersonalData ? "var(--primary-green)" : "var(--text-medium)",
              border: element.isPersonalData ? "1px solid var(--primary-green)" : "1px solid var(--border-light)",
              "& .MuiChip-icon": {
                color: element.isPersonalData ? "var(--primary-green)" : "var(--text-light)",
              },
            }}
          />
        </Box>

        <Typography variant="body2" sx={{ color: "var(--text-medium)", mb: 1 }}>
          {t("type")}: {element.dataElementType}
        </Typography>

        <Box mb={2}>
          <Typography variant="body2" sx={{ color: "var(--text-medium)" }} gutterBottom>
            {t("value")}:
          </Typography>
          <Box
            sx={{
              backgroundColor: showPersonalData && originalValue ? "var(--bg-accent)" : "var(--bg-secondary)",
              border: "1px solid",
              borderColor: showPersonalData && originalValue ? "var(--primary-green)" : "var(--border-light)",
              borderRadius: 1,
              padding: "8px 12px",
              fontFamily: "monospace",
              fontSize: "0.875rem",
              color: showPersonalData && originalValue ? "var(--primary-green)" : "var(--text-medium)",
              fontWeight: showPersonalData && originalValue ? 500 : 400,
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "all 0.3s ease",
            }}
          >
            <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getDisplayValue()}</Box>
            {element.isPersonalData && element.vaultId && (
              <Tooltip title={t("personalDataTooltip")}>
                <IconButton
                  size="small"
                  onClick={showPersonalData ? handleHidePersonalData : handleShowPersonalData}
                  disabled={loading}
                  sx={{
                    ml: 1,
                    p: 0.5,
                    color: showPersonalData && originalValue ? "var(--primary-green)" : "var(--text-light)",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={16} />
                  ) : showPersonalData ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorIcon />}>
            {error}
          </Alert>
        )}

        {showPersonalData && originalValue && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
              backgroundColor: "var(--bg-secondary)",
              border: "1px solid var(--border-light)",
              borderRadius: "var(--radius-sm)",
              "& .MuiAlert-icon": {
                color: "var(--text-medium)",
              },
              "& .MuiAlert-message": {
                color: "var(--text-medium)",
                fontSize: "0.875rem",
              },
            }}
          >
            {t("personalDataRevealed")}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default PersonalDataElement;
